/**
 * SessionsPage — active and historical session monitoring.
 *
 * Fixes applied vs original:
 *  1. drowsiness_count / yawn_count now show correctly — server now embeds them.
 *  2. Supervisor cover sessions visible in the table.
 *  3. Timestamps honour dashboard timezone setting.
 *  4. Status filter passed to /sessions endpoint (was only client-side).
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Alert, CircularProgress, Tooltip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Select, MenuItem, FormControl, InputLabel, Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import api from '../api/client';
import useDetectionSettings, { enabled } from '../hooks/useDetectionSettings';
import useTimezone from '../hooks/useTimezone';
import { ensureTz, formatDateTime, formatDuration } from '../utils/format';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const getSessions = (params = {}) =>
  api.get('/api/v1/sessions', { params }).then((r) => r.data);

const forceLogout = (sessionId, reason) =>
  api.post(`/api/v1/sessions/${sessionId}/force-logout`, { reason }).then((r) => r.data);

// ---------------------------------------------------------------------------
// ForceLogoutDialog
// ---------------------------------------------------------------------------

function ForceLogoutDialog({ open, onClose, session, onDone }) {
  const [reason, setReason] = useState('supervisor_override');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setReason('supervisor_override'); setError(''); }
  }, [open]);

  const handle = async () => {
    setSaving(true);
    setError('');
    try {
      await forceLogout(session.session_id || session.id, reason);
      onDone(`Operator ${session.operator?.employee_id} logged out.`);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Force logout failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Force Logout</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" sx={{ mb: 2 }}>
          You are about to force-logout operator{' '}
          <strong>{session?.operator?.name} ({session?.operator?.employee_id})</strong>{' '}
          on device <strong>{session?.device_name}</strong>.
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Reason</InputLabel>
          <Select value={reason} label="Reason" onChange={(e) => setReason(e.target.value)}>
            <MenuItem value="supervisor_override">Supervisor override</MenuItem>
            <MenuItem value="end_of_shift">End of shift</MenuItem>
            <MenuItem value="safety_incident">Safety incident</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handle} variant="contained" color="error" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Force Logout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function elapsed(isoStart) {
  if (!isoStart) return '—';
  const secs = Math.floor((Date.now() - new Date(ensureTz(isoStart)).getTime()) / 1000);
  if (secs < 0) return '—';
  if (secs < 5) return 'just now';
  return formatDuration(secs);
}

const fmtDuration = formatDuration;

const STATUS_COLOR = {
  active: 'success',
  break: 'warning',
  logged_out: 'default',
  forced_logout: 'error',
  expired: 'default',
};

// ---------------------------------------------------------------------------
// SessionsPage
// ---------------------------------------------------------------------------

export default function SessionsPage() {
  const detectionSettings = useDetectionSettings();
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('active,break');

  const [logoutTarget, setLogoutTarget] = useState(null);
  const [snack, setSnack] = useState('');
  const [tick, setTick] = useState(0);

  const tz = useTimezone();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 200, tz };
      if (filterStatus) params.status = filterStatus;
      const data = await getSessions(params);
      setSessions(data.items ?? data);
      setTotal(data.total ?? (data.items ?? data).length);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load sessions.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, tz]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s
  useEffect(() => {
    const t = setInterval(() => { load(); setTick((n) => n + 1); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Update elapsed timers every 10s
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 10000);
    return () => clearInterval(t);
  }, []);

  const handleLogoutDone = (msg) => {
    setSnack(msg);
    load();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5" fontWeight={700}>Active Sessions</Typography>
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={`Times shown in: ${tz}`}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Auto-refreshes every 30s · {total} session{total !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="active,break">Active + On Break</MenuItem>
              <MenuItem value="active">Active Only</MenuItem>
              <MenuItem value="break">On Break Only</MenuItem>
              <MenuItem value="">All (incl. closed)</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && sessions.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Operator</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>{`Checked In (${tz})`}</TableCell>
                <TableCell>{`Checked Out (${tz})`}</TableCell>
                <TableCell>Duration</TableCell>
                {enabled(detectionSettings, 'drowsiness') && <TableCell align="center">💤 Drowsy</TableCell>}
                {enabled(detectionSettings, 'yawning') && <TableCell align="center">🥱 Yawns</TableCell>}
                {enabled(detectionSettings, 'distracted') && <TableCell align="center">👀 Distracted</TableCell>}
                {enabled(detectionSettings, 'presence') && <TableCell align="center">📍 Presence</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No sessions found.
                  </TableCell>
                </TableRow>
              ) : sessions.map((s) => (
                <TableRow key={s.session_id || s.id} hover>
                  <TableCell>{s.operator?.name || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {s.operator?.employee_id || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={s.operator?.role || 'operator'}
                      size="small"
                      variant="outlined"
                      color={s.operator?.role === 'supervisor' || s.operator?.role === 'admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{s.device_name || s.device_id?.slice(0, 8) || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.status}
                      color={STATUS_COLOR[s.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(s.checked_in_at, tz)}</TableCell>
                  <TableCell>{formatDateTime(s.checked_out_at, tz)}</TableCell>
                  <TableCell>
                    {s.status === 'active' || s.status === 'break'
                      ? elapsed(s.checked_in_at)
                      : fmtDuration(s.duration_seconds)}
                  </TableCell>
                  {enabled(detectionSettings, 'drowsiness') && <TableCell align="center">
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={(s.drowsiness_count || 0) > 0 ? 'error' : 'text.secondary'}
                    >
                      {s.drowsiness_count ?? 0}
                    </Typography>
                  </TableCell>}
                  {enabled(detectionSettings, 'yawning') && <TableCell align="center">
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={(s.yawn_count || 0) > 3 ? 'warning.main' : 'text.secondary'}
                    >
                      {s.yawn_count ?? 0}
                    </Typography>
                  </TableCell>}
                  {enabled(detectionSettings, 'distracted') && <TableCell align="center">
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={(s.distracted_count || 0) > 0 ? 'secondary.main' : 'text.secondary'}
                    >
                      {s.distracted_count ?? 0}
                    </Typography>
                  </TableCell>}
                  {enabled(detectionSettings, 'presence') && <TableCell align="center">
                    {s.presence_summary ? `${s.presence_summary.presence_percent}%` : '—'}
                  </TableCell>}
                  <TableCell align="right">
                    {(s.status === 'active' || s.status === 'break') && (
                      <Tooltip title="Force Logout">
                        <IconButton size="small" color="error"
                          onClick={() => setLogoutTarget(s)}>
                          <LogoutIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ForceLogoutDialog
        open={Boolean(logoutTarget)}
        onClose={() => setLogoutTarget(null)}
        session={logoutTarget}
        onDone={handleLogoutDone}
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        message={snack}
      />
    </Box>
  );
}
